import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import employeeService from '../../api/services/employeeService';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import SafeImage from '../../components/common/SafeImage';

const AddorUpdate = () => {
    const { id } = useParams(); // Get ID from URL
    const navigate = useNavigate(); // Get navigate function
    const [loading, setLoading] = useState(false);
    const [fetchingEmployee, setFetchingEmployee] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [profileImage, setProfileImage] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState('');

    const [employeeInfo, setEmployeeInfo] = useState({
        firstName: '',
        email: '',
        phoneNumber: '',
    });

    useEffect(() => {
        if (id) {
            fetchEmployeeData();
        }
    }, [id]);

    // Set preview if employee has an image
    useEffect(() => {
        if (id && employeeInfo.profile_image) {
            setProfileImagePreview(
                employeeInfo.profile_image.startsWith('http')
                    ? employeeInfo.profile_image
                    : (import.meta.env.VITE_API_URL || 'http://localhost:8000') + employeeInfo.profile_image
            );
        }
    }, [id, employeeInfo.profile_image]);

    const fetchEmployeeData = async () => {
        try {
            setFetchingEmployee(true);
            const result = await employeeService.getEmployeeById(id);
            
            if (result.success) {
                const employee = result.data;
                
                const newEmployeeInfo = {
                    firstName: employee.user?.name || '',
                    email: employee.user?.email || '',
                    phoneNumber: employee.user?.phone_no || 'N/A',
                    profile_image: employee.user?.profile_image || '',
                };
                
                setEmployeeInfo(newEmployeeInfo);
            } else {
                toast.error(result.message || 'Failed to fetch employee data');
            }
        } catch (error) {
            console.error('Error fetching employee:', error);
            toast.error('Failed to fetch employee data');
        } finally {
            setFetchingEmployee(false);
        }
    };

    const handleInfoChange = (e) => {
        const { name, value } = e.target;
        setEmployeeInfo(prevInfo => ({
            ...prevInfo,
            [name]: value,
        }));
    };

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            setProfileImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched({
            firstName: true,
            email: true,
            phoneNumber: true
        });
        // Validate required fields
        if (Object.values(validateForm()).length > 0) {
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(employeeInfo.email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        // Validate phone number (international format)
        if (!employeeInfo.phoneNumber) {
            toast.error('Phone number is required');
            return;
        } else if (!/^\+?[1-9]\d{7,14}$/.test(employeeInfo.phoneNumber)) {
            toast.error('Please enter a valid international phone number (e.g., +1234567890)');
            return;
        }

        try {
            setLoading(true);
            const employeeData = {
                name: employeeInfo.firstName,
                email: employeeInfo.email,
                phone_no: employeeInfo.phoneNumber,
            };
            let result;
            if (id) {
                // Update existing employee info (without image)
                result = await employeeService.updateEmployee(id, employeeData);
                if (result.success) {
                    // If a new profile image is selected, update it separately
                    if (profileImage) {
                        const imgRes = await employeeService.updateProfileImage(id, profileImage);
                        setLoading(false);
                        if (imgRes.success) {
                            toast.success(result.message || 'Employee updated successfully');
                            navigate('/employee');
                        } else {
                            toast.error(imgRes.message || 'Failed to update profile image');
                        }
                    } else {
                        setLoading(false);
                        toast.success(result.message || 'Employee updated successfully');
                        navigate('/employee');
                    }
                } else {
                    setLoading(false);
                    const errorMessage = result.errors ? 
                        Object.values(result.errors).flat().join(', ') : 
                        result.message || 'Operation failed';
                    toast.error(errorMessage);
                }
            } else {
                // Add new employee (image included in FormData)
                let formData;
                if (profileImage) {
                    formData = new FormData();
                    Object.entries(employeeData).forEach(([key, value]) => {
                        formData.append(key, value);
                    });
                    formData.append('profile_image', profileImage);
                }
                result = await employeeService.createEmployee(profileImage ? formData : employeeData);
                setLoading(false);
                if (result.success) {
                    toast.success(result.message || 'Employee created successfully');
                    navigate('/employee');
                } else {
                    const errorMessage = result.errors ? 
                        Object.values(result.errors).flat().join(', ') : 
                        result.message || 'Operation failed';
                    toast.error(errorMessage);
                }
            }
        } catch (error) {
            setLoading(false);
            toast.error('Failed to save employee: ' + (error.message || 'Unknown error'));
        }
    };

    // Validation function
    const validateForm = () => {
        const newErrors = {};
        // Name
        if (!employeeInfo.firstName) {
            newErrors.firstName = 'Name is required';
        }
        // Email
        if (!employeeInfo.email) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employeeInfo.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        // Phone
        if (!employeeInfo.phoneNumber) {
            newErrors.phoneNumber = 'Phone number is required';
        } else if (!/^\+?[1-9]\d{7,14}$/.test(employeeInfo.phoneNumber)) {
            newErrors.phoneNumber = 'Please enter a valid international phone number (e.g., +1234567890)';
        }
        return newErrors;
    };

    // Live validation: validate on every change
    useEffect(() => {
        setErrors(validateForm());
        // eslint-disable-next-line
    }, [employeeInfo]);

    // Determine if it's an add or edit operation
    const isEdit = !!id;

    if (fetchingEmployee) {
        return (
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body text-center">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading employee data...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="mb-4">
                        <h2 className="fw-bold">{isEdit ? 'Edit Employee' : 'Add New Employee'}</h2>
                    </div>
                    
                    <form className="theme-form theme-form-2 mega-form" onSubmit={handleSubmit}>
                        <div className="mb-4 row align-items-center">
                            <label className="form-label-title col-sm-3 mb-0">
                                Name <span className="text-danger">*</span>
                            </label>
                            <div className="col-sm-9">
                                <input
                                    className="form-control"
                                    type="text"
                                    name="firstName"
                                    value={employeeInfo.firstName}
                                    onChange={handleInfoChange}
                                    onBlur={() => setTouched(prev => ({ ...prev, firstName: true }))}
                                    required
                                />
                                {touched.firstName && errors.firstName && (
                                    <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '4px' }}>
                                        {errors.firstName}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4 row align-items-center">
                            <label className="form-label-title col-sm-3 mb-0">
                                Email Address <span className="text-danger">*</span>
                            </label>
                            <div className="col-sm-9">
                                <input
                                    className="form-control"
                                    type="email"
                                    name="email"
                                    value={employeeInfo.email}
                                    onChange={handleInfoChange}
                                    onBlur={() => setTouched(prev => ({ ...prev, email: true }))}
                                    required
                                />
                                {touched.email && errors.email && (
                                    <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '4px' }}>
                                        {errors.email}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4 row align-items-center">
                            <label className="form-label-title col-sm-3 mb-0">
                                Phone Number <span className="text-danger">*</span>
                            </label>
                            <div className="col-sm-9">
                                <PhoneInput
                                    country={'us'}
                                    value={employeeInfo.phoneNumber}
                                    onChange={phone => setEmployeeInfo(prev => ({ ...prev, phoneNumber: phone }))}
                                    inputProps={{
                                        name: 'phoneNumber',
                                        required: true,
                                        className: 'form-control',
                                        onBlur: () => setTouched(prev => ({ ...prev, phoneNumber: true })),
                                    }}
                                    containerStyle={{ width: '100%' }}
                                    inputStyle={{ width: '100%', height: 45, fontSize: '1rem' }}
                                    dropdownStyle={{ zIndex: 9999 }}
                                />
                                {touched.phoneNumber && errors.phoneNumber && (
                                    <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '4px' }}>
                                        {errors.phoneNumber}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4 row align-items-center">
                            <label className="form-label-title col-sm-3 mb-0">
                                Profile Image
                            </label>
                            <div className="col-sm-9">
                                <input
                                    className="form-control"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleProfileImageChange}
                                />
                                {profileImagePreview && (
                                    <SafeImage
                                        src={profileImagePreview}
                                        alt="Profile Preview"
                                        style={{ width: 80, height: 80, objectFit: 'cover', marginTop: 8, borderRadius: 8 }}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="mt-5 mb-4">
                            <button 
                                className="btn btn-animation" 
                                type="submit" 
                                disabled={loading || Object.values(errors).length > 0}
                                style={{
                                    backgroundColor: (loading || Object.values(errors).length > 0) ? '#6c757d' : '',
                                    color: (loading || Object.values(errors).length > 0) ? '#ffffff' : '',
                                    cursor: (loading || Object.values(errors).length > 0) ? 'not-allowed' : 'pointer',
                                    opacity: (loading || Object.values(errors).length > 0) ? 0.6 : 1
                                }}
                            >
                                {loading ? 'Saving...' : (isEdit ? 'Update' : 'Submit')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default AddorUpdate;